import { HISTORY_IN_QUERY_SUGGESTIONS_MAX_LENGTH } from '../components/Search/utils/searchHistory/constants';
import { findInSearchHistory } from '../components/Search/utils/searchHistory/find';
import { getMatchInSearchHistory } from '../components/Search/utils/searchHistory/match';
import type { QuerySuggestion, SuggestionHit } from '../interfaces/algolia';
import type {
  StaticSuggestionType,
  Suggestion,
  SuggestionType,
} from '../interfaces/suggestions';
import { formatSuggestion } from './formatSuggestion';

// Add suggestions from history corresponding to the query
const addSuggestionsFromHistory = (
  termQuery: string,
  hits: SuggestionHit[],
  history: string[],
  maxSuggestions?: number,
): SuggestionHit[] => {
  // Get suggestions from history by query
  const matchedQueries = getMatchInSearchHistory(
    termQuery,
    history,
    maxSuggestions,
  );

  // Filter history suggestions if already exist
  const filteredSuggestions = matchedQueries.filter(
    (query) => !hits.some((sugg) => sugg.query === query),
  );

  // Convert to suggestion type
  const newSuggestions = filteredSuggestions.map((query) => ({
    objectID: query,
    query,
  }));

  // Return new suggestions in results
  return [...newSuggestions, ...hits];
};

// Format suggestions to display
export const createSuggestions = <T extends SuggestionHit | QuerySuggestion>(
  {
    hits,
    type,
    termQuery = '',
    highlight = false,
    history = [],
    autocomplete = false,
  }: {
    hits: SuggestionHit[] | QuerySuggestion[];
    type: SuggestionType | Extract<StaticSuggestionType, 'product'>;
    termQuery?: string;
    highlight?: boolean;
    history?: string[];
    autocomplete?: boolean;
  },
  format?: (suggestion: T) => Suggestion,
) => {
  let rawSuggestions = hits;

  if (!autocomplete && history.length) {
    rawSuggestions = addSuggestionsFromHistory(
      termQuery,
      hits,
      history,
      HISTORY_IN_QUERY_SUGGESTIONS_MAX_LENGTH,
    );
  }

  const doFormat =
    format ||
    ((suggestion) =>
      formatSuggestion({
        name: suggestion.query,
        objectID: suggestion.objectID,
        termQuery,
        type,
        highlight,
        queryCategory:
          type === 'query' && autocomplete
            ? (suggestion as QuerySuggestion).category
            : undefined,
      }));

  const suggestions = rawSuggestions
    ?.map(
      (suggestion) =>
        ({
          ...doFormat(suggestion as T),
          inHistory: findInSearchHistory(suggestion.query, history),
        }) as Suggestion,
    )
    // Reorder for history first
    .sort((a, b) => Number(b.inHistory) - Number(a.inHistory));

  return suggestions;
};
